import React, { useCallback, useEffect, useState, useRef } from "react";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import { fetchEmployeeLeaves } from "../../../Apis/Employee-api";
import { MAIN_URL } from "../../../Configurations/Urls";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout4 from "../../DataLayouts/Layout4";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { fetchLeaveTypes } from "../../../Apis/Leave-api";
import {
  applyClientToolbarFilters,
  asOptionList,
  mergeToolbarFilters,
  ToolbarFilterSelect,
  useToolbarFilterChange,
} from "../../../Components/Table/toolbarFilters";
import { Box, Typography } from "@mui/material";
import InboxIcon from "@mui/icons-material/Inbox";
import { formatLeaveDateDisplay } from "../../../lib/formatLeaveDateDisplay";

const DEFAULT_COLUMNS = [
  {
    field: "employee_name",
    label: "Employee Name",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "employee_code",
    label: "Employee Code",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "leave_start_date",
    label: "Leave Start Date",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "leave_end_date",
    label: "Leave End Date",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "leave_type",
    label: "Leave Type",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "leave_status",
    label: "Leave Status",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "approval_date",
    label: "Approval Date",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];


const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
  prev_page: null,
  next_page: null,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_person_leave_id",
  sortOrder: "desc",
};

const EMPLOYEE_LEAVE_TOOLBAR_FILTERS = [
  { urlKey: "leave_type_id", field: "leave_type" },
  { urlKey: "leave_duration_type", field: "leave_duration_type" },
  { urlKey: "leave_status", field: "leave_status" },
];

const LEAVE_DURATION_OPTIONS = [
  { value: "full_day", label: "Full Day" },
  { value: "half_day", label: "Half Day" },
  { value: "short_leave", label: "Short Leave" },
];

const LEAVE_STATUS_OPTIONS = [
  { value: "Pending", label: "Pending" },
  { value: "Approved", label: "Approved" },
  { value: "Rejected", label: "Rejected" },
];

// Helper function to build filters from URL search parameters
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals", "in", "not_in"].includes(operator)) {
          // Keep original casing — leave type / FK labels are case-sensitive in some DBs
          filterValue = filterValue.split(",").map((v) => v.trim()).filter(Boolean);
          if (filterValue.length === 1) {
            filterValue = filterValue[0];
          }
        } else if (
          ["between", "not_between", "date_between", "date_not_between"].includes(
            operator
          )
        ) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  return filters;
};





function EmployeeLeaveList() {
  const [leaves, setLeaves] = useState([]);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [loading, setLoading] = useState(true);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const handleToolbarFilterChange = useToolbarFilterChange(setSearchParams);

  const capitalize = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str?.slice(1).toLowerCase();
  };

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!org?.organization_id) return;
    let cancelled = false;
    fetchLeaveTypes(org.organization_id)
      .then((data) => {
        if (cancelled) return;
        setLeaveTypes(asOptionList(data, ["leavetypes", "data"]));
      })
      .catch((error) => {
        console.error("Failed to load leave types", error);
      });
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  // Load table configuration: metadata for columns, then saved layout from general-datagrids
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        let columnsFromMetadata = [];
        const metaRes = await fetch(
          `${MAIN_URL}/api/metadata/employee_leaves`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (metaRes.ok) {
          const meta = await metaRes.json();
          const fields = meta.fields || [];
          const displayFields = fields.filter((f) => f.is_display_field);
          const useFields = displayFields.length > 0 ? displayFields : fields;
          columnsFromMetadata = useFields
            .filter((f) => f.field_key !== "organization_person_id")
            .map((f) => ({
              field: f.field_key,
              label: f.label || f.field_key,
              visible: true,
              sortable: true,
              filterable: !!f.is_filterable,
              width: 150,
              field_type: f.field_type,
            }));
        }

        const configRes = await fetch(
          `${MAIN_URL}/api/general-datagrids`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `employee_leaves_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              const savedCols = serverCfg.columns;
              const hasEmployeeName = savedCols.some(
                (c) => (c.field || c.key) === "employee_name"
              );
              const hasEmployeeCode = savedCols.some(
                (c) => (c.field || c.key) === "employee_code"
              );
              if (!hasEmployeeName || !hasEmployeeCode) {
                const nameCodeCols = DEFAULT_COLUMNS.filter(
                  (c) =>
                    c.field === "employee_name" || c.field === "employee_code"
                ).map((c) => ({
                  key: c.field,
                  field: c.field,
                  label: c.label,
                  width: c.width ?? 150,
                  visible: true,
                  order: -1,
                  required: false,
                }));
                setConfigColumns([...nameCodeCols, ...savedCols]);
              } else {
                setConfigColumns(savedCols);
              }
            } else if (columnsFromMetadata.length > 0) {
              setConfigColumns(columnsFromMetadata);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else if (columnsFromMetadata.length > 0) {
            setConfigColumns(columnsFromMetadata);
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        } else if (columnsFromMetadata.length > 0) {
          setConfigColumns(columnsFromMetadata);
        } else {
          setConfigColumns(DEFAULT_COLUMNS);
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // load employeeLeave data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = mergeToolbarFilters(
          buildFiltersFromParams(searchParams),
          searchParams,
          EMPLOYEE_LEAVE_TOOLBAR_FILTERS
        );
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "organization_person_leave_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        // Try new POST endpoint first, fallback to old GET endpoint if needed
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/employee-leave/filters`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          data = result.data ?? [];
          if (result.success && result.pagination) {
            const pageSize =
              result.pagination.pageSize || result.pagination.limit || 10;
            setPaginationData({
              page:
                result.pagination.page ||
                Number.parseInt(searchParams.get("page"), 10) ||
                1,
              pageSize,
              total: result.pagination.total || 0,
              totalPages: result.pagination.totalPages || 1,
              hasNext: result.pagination.hasNext || false,
              hasPrev: result.pagination.hasPrev || false,
              prev_page: result.pagination.prev_page ?? null,
              next_page: result.pagination.next_page ?? null,
            });
          } else if (!result.success) {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          // Fallback to old GET endpoint if POST endpoint doesn't exist yet
          const oldData = await fetchEmployeeLeaves(org.organization_id);
          data = applyClientToolbarFilters(
            oldData?.employeeleaves?.data || [],
            searchParams,
            EMPLOYEE_LEAVE_TOOLBAR_FILTERS
          );
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const b = (data ?? []).map((item) => {
          const startDate = formatLeaveDateDisplay(item?.leave_start_date);
          const endDate = formatLeaveDateDisplay(item?.leave_end_date);
          const approval_date = formatLeaveDateDisplay(item?.approval_date);
          const rejection_date = formatLeaveDateDisplay(item?.rejection_date);

          return {
            ...item,
            id: item.organization_person_leave_id,
            employee_name: item?.employee_name ?? item?.full_name ?? "",
            employee_code: item?.employee_code ?? item?.person_code ?? "",
            full_name: item?.employee_name ?? item?.full_name ?? "",
            profile_image_url: item?.profile_image_url || "",
            leave_start_date: startDate,
            leave_start_time:
              item?.leave_start_time == null ? "" : item?.leave_start_time,
            leave_end_time:
              item?.leave_end_time == null ? "" : item?.leave_end_time,
            total_leave_hours:
              item?.total_leave_hours == null ? "" : item?.total_leave_hours,
            leave_end_date: endDate,
            rejection_date: rejection_date,
            approval_date: approval_date,
         
          };
        });

        setLeaves(b);
      } catch (err) {
        if (err.name === "AbortError") return;
        
        if (err.response && err.response.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          console.error("Failed to fetch employee leaves:", err);
          toast.error("Failed to fetch employee leaves");
          setLeaves([]);
          setPaginationData(DEFAULT_PAGINATION);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org, searchParams]);

  const deleteEmployeeLeave = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/employee-leave/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        if (response.ok) {
          toast.success(data.message || "Employee leave deleted successfully");
          // Refresh data after deletion by triggering useEffect
          setSearchParams((prev) => new URLSearchParams(prev));
        } else {
          const errorMessage =
            data.message || data.error || "Failed to delete employee leave";
          toast.error(errorMessage);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          toast.error(
            error.response?.data?.error || "Failed to delete employee leave"
          );
        }
        console.error("Delete failed:", error);
      }
    },
    [org?.organization_id, setSearchParams]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(
        `/leave/employee-leaves/edit/${item.id || item.organization_person_leave_id}`
      );
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(
        `/leave/employee-leaves/view/${item.id || item.organization_person_leave_id}`
      );
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  const handleFilterChange = useCallback(() => {
    // Intentionally no-op.
    // Customisetable already writes filter_* / page into the URL via updateURL.
    // Rewriting searchParams here races with pagination (page clicks) and resets page to 1.
  }, []);

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Employee Leave"}
        btnName={"Add Employee Leave"}
        Data={leaves}
        delete_action={"EMPLOYEE_LEAVE_DELETE"}
        add_action={"EMPLOYEE_LEAVE_ADD"}
        tableHeaders={[
          {
            name: "Employee Name",
            value_key: "fullName",
            textStyle: "capitalize",
          },
          { name: "Leave Starts", value_key: "startDate" },
          { name: "Leave Ends", value_key: "endDate" },
          {
            name: "Employee Remarks",
            value_key: "employee_remarks",
            textStyle: "capitalize",
          },
          { name: "Leave Status", value_key: "leave_status" },
        ]}
        Icons={[
          <PersonIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <FormatAlignJustifyIcon color="primary" />,
          <CategoryIcon sx={{ color: "text.secondary" }} />,
          <DateRangeIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Employee Leaves",
          "Employee Leaves",
          "Add Employee Leaves",
          "Employee Leaves",
        ]}
        Route={"/leave/employee-leaves"}
        setData={setLeaves}
        DeleteFunc={deleteEmployeeLeave}
      />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="employee_leaves"
          primaryKey="organization_person_leave_id"
          heading="Employee Leaves"
          data={leaves}
          sortname="leave_status"
          showActions={true}
          Route="/leave/employee-leaves"
          DeleteFunc={deleteEmployeeLeave}
          EditFunc={handleEdit}
          edit_delete_action={["EMPLOYEE_LEAVE_DELETE", "EMPLOYEE_LEAVE_EDIT"]}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          onFilterChange={handleFilterChange}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="employee_leaves"
          toolbarExtras={
            <>
              <ToolbarFilterSelect
                label="Leave Type"
                urlKey="leave_type_id"
                allLabel="All leave types"
                options={leaveTypes}
                valueKey="organization_leave_type_id"
                labelKey="leave_type_name"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
              <ToolbarFilterSelect
                label="Leave Duration Type"
                urlKey="leave_duration_type"
                allLabel="All duration types"
                options={LEAVE_DURATION_OPTIONS}
                valueKey="value"
                labelKey="label"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
              <ToolbarFilterSelect
                label="Leave Status"
                urlKey="leave_status"
                allLabel="All leave statuses"
                options={LEAVE_STATUS_OPTIONS}
                valueKey="value"
                labelKey="label"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
            </>
          }
          config={{
            ...(tableConfig || {}),
            sort: {
              field: searchParams.get("sortBy") || "organization_person_leave_id",
              direction: searchParams.get("sortOrder") || "desc",
            },
          }}
        />
      )}
    </>
  );
}

export default EmployeeLeaveList;
